import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { TOUR_STYLED_PRODUCT_TYPE_TITLES } from 'constants/tours'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { ComponentProps } from 'react'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

export type TourStyledSignatureSeriesProductTypeInfoModalResult = 'primary-action' | undefined

type InheritedModalProps = Pick<ComponentProps<typeof Modal>, 'primaryActionText' | 'primaryActionTo'>

interface Props extends InheritedModalProps {
}

function TourStyledSignatureSeriesProductTypeInfoModal({
  primaryActionText,
  primaryActionTo,
}: Props) {
  const modalElement = useModalElementContext<TourStyledSignatureSeriesProductTypeInfoModalResult>()
  const intl = useIntl()

  const handlePrimaryClick = useCallback(() => {
    modalElement.resolve('primary-action')
  }, [modalElement])

  const title = <>{intl.formatMessage(
    {
      id: 'Ro_v6tXKltWR2iMPNzVY',
      defaultMessage: '{brandName} Tour Styles',
      description: 'Modal title for tour styles information with brand name',
    },
    { brandName: config.brandConfig.name },
  )}
  </>

  const learnMoreText = intl.formatMessage(
    {
      id: '1sr6TGl20ygI3kXDsBOF',
      defaultMessage: 'Learn more',
      description: 'Button text to navigate to the Signature Series campaign page',
    },
  )

  const descriptionText = intl.formatMessage(
    {
      id: 'lOa_u8QtY59tieP7Usl_',
      defaultMessage: 'These celebrity-hosted, ultra-exclusive journeys are designed and led by some of Australia’s most-loved personalities. With once-in-a-lifetime itineraries, Luxury Escapes’ Signature Series take you to the world’s top destinations on the adventure of your dreams.',
      description: 'Description of the Signature Series tour style explaining celebrity-hosted luxury journeys',
    },
  )

  const oneDepartureHeading = intl.formatMessage(
    {
      id: 'Y9Gs1YqckofV2-csSZwJ',
      defaultMessage: 'One Departure Only',
      description: 'Feature heading for Signature Series tours highlighting single departure date',
    },
  )

  const oneDepartureDescription = intl.formatMessage(
    {
      id: 'tbkA4_Sqnu3dcyNQtSSM',
      defaultMessage: 'Each tour has just one departure date, ensuring that every trip is a unique, once-in-a-lifetime experience.',
      description: 'Feature description explaining single departure date for Signature Series tours',
    },
  )

  const iconicItinerariesHeading = intl.formatMessage(
    {
      id: 'aQsSSrwaIMQKhfe_1C4l',
      defaultMessage: 'Iconic Itineraries',
      description: 'Feature heading for Signature Series tours highlighting iconic travel itineraries',
    },
  )

  const iconicItinerariesDescription = intl.formatMessage(
    {
      id: 'uxNup8f9eTWbV7lUI2vm',
      defaultMessage: 'Travellers will enjoy iconic bucket-list experiences in each destination, capturing all the highlights alongside exclusive, money-can’t-buy moments that go beyond ordinary travel expectations.',
      description: 'Feature description of iconic itineraries offered in Signature Series tours',
    },
  )

  const unmatchedLuxuryHeading = intl.formatMessage(
    {
      id: 'LrzW63Lh8EvoIlrJDRSI',
      defaultMessage: 'Unmatched Luxury',
      description: 'Feature heading for Signature Series tours highlighting luxury accommodation',
    },
  )

  const unmatchedLuxuryDescription = intl.formatMessage(
    {
      id: 'dM7qWTFOyeEJpq-tZ0Pv',
      defaultMessage: 'All hotels and resorts are meticulously handpicked for their exceptional quality, comfort, location, and service. These properties have been chosen to elevate your overall travel experience.',
      description: 'Feature description of handpicked luxury hotels in Signature Series tours',
    },
  )

  return <Modal
    size="L"
    title={title}
    subtitle={TOUR_STYLED_PRODUCT_TYPE_TITLES.signature_series_tour}
    primaryActionText={primaryActionText}
    primaryActionTo={primaryActionTo}
    onPrimaryActionClick={!primaryActionTo ? handlePrimaryClick : undefined}
    secondaryActionText={learnMoreText}
    secondaryActionTo="/campaign/signature-series"
  >
    <VerticalSpacer gap={24}>
      <BodyText variant="medium" colour="neutral-two">
        {descriptionText}
      </BodyText>
      <Group
        direction="vertical"
        tabletDirection="horizontal"
        tabletHorizontalAlign="stretch"
        gap={24}
      >
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold" colour="neutral-one">
            {oneDepartureHeading}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {oneDepartureDescription}
          </BodyText>
        </VerticalSpacer>
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold" colour="neutral-one">
            {iconicItinerariesHeading}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {iconicItinerariesDescription}
          </BodyText>
        </VerticalSpacer>
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold" colour="neutral-one">
            {unmatchedLuxuryHeading}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {unmatchedLuxuryDescription}
          </BodyText>
        </VerticalSpacer>
      </Group>
    </VerticalSpacer>
  </Modal>
}

export default TourStyledSignatureSeriesProductTypeInfoModal
